import { useMemo, useState, useEffect } from 'react';
import {
  ACCEPTABLE_URL_PARAMS_TIER3,
  FilterTypes,
  INTIAL_FILTERS,
} from '../../constants/initialFilters';
import {
  ComplexFilterOptions,
  QUERY_FILTERS_OPTIONS,
  RANGE_FILTER_QUERY_OPTIONS,
  SimpleAndRegularFilterOptions,
} from '../../constants/queryFiltersConfig';
import { getHashParams } from '../../utils/TransformURL';
import useFilterData from '../Fetch/useGetFiltersFromLS';
// import useHashFragment from '../useHashFragment';

type CarlineTypes = {
  [key: string]: string[];
};

const useParseHashFilters = (): FilterTypes | null => {
  const filterLSData = useFilterData();
  const [hashState, setHashState] = useState({
    hashParams: decodeURIComponent(window.location.hash),
    version: 0,
  });
  useEffect(() => {
    const updateHash = () => {
      const newHash = decodeURIComponent(window.location.hash);
      setHashState((prev) => ({
        hashParams: newHash,
        version: prev.version + 1,
      }));
    };

    const handlePopState = () => {
      updateHash();
    };

    window.addEventListener('hashchange', updateHash);
    window.addEventListener('popstate', handlePopState); // Handle pushState changes

    return () => {
      window.removeEventListener('hashchange', updateHash);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);
  const { hashParams } = hashState;

  // if it is Tier 3 grab params from # not ?
  // const cleanedURL = decodeURIComponent(window.location.href);
  // LATER IF THEY WANT TO UPDATE URL WITH THE CLEANED URL
  // window.history.pushState({}, '', cleanedURL);
  const searchParams = getHashParams(hashParams);

  const hasFilterParams = ACCEPTABLE_URL_PARAMS_TIER3.some((param) => searchParams.has(param));
  const isOnlyFilterTypeParam = searchParams.has('type') && !hasFilterParams;

  // Function that will transform filter that have label Complex (carline), because each query
  // value will effect on carline + body type
  const complexFiltersProcessor = (singleFilterData: ComplexFilterOptions, type: string) => {
    const filterQueryValues = searchParams
      .get(type)
      ?.split(',')
      .map((param) => param.toLocaleLowerCase());
    const complexFilterOptions = Object.keys(singleFilterData.options);
    const acceptableFilters = filterQueryValues
      ?.filter((toCheck) => complexFilterOptions.includes(toCheck))
      .map((singleFilter) => singleFilterData.options[singleFilter])
      .reduce((acc, curr) => {
        Object.entries(curr).forEach(([key, value]) => {
          acc[key] = [...(acc[key] || []), ...value];
        });
        return acc;
      }, {});
    if (type === 'carline-group' && acceptableFilters) return acceptableFilters.modelFamily;
    return acceptableFilters;
  };

  // Function that will transform filter that have label Regular - filters that can have multiply
  // values in one array
  const regularFilterProcessor = (
    singleFilterData: SimpleAndRegularFilterOptions,
    type: string,
  ) => {
    const filterQueryValues = searchParams
      .get(type)
      ?.split(',')
      .map((param) => {
        if (param === 'allroad-quattro' || param === 'allroadquattro') return 'allroad-quattro';
        if (param === 'grey') return 'gray';
        return param.toLocaleLowerCase();
      });
    const acceptableFilters = [
      ...new Set(
        filterQueryValues?.filter((toCheck) => singleFilterData.options.includes(toCheck)),
      ),
    ];
    return acceptableFilters;
  };

  // Function that will transform filter that have label Regular - filters that can have multiply
  // values in one array and wont check options because the options are dynamic
  const regularFilterProcessorWithoutCheck = (
    singleFilterData: SimpleAndRegularFilterOptions,
    type: string,
  ) => {
    const filterQueryValues = searchParams
      .get(type)
      ?.split(',')
      .map((param) => {
        return param;
      });
    const acceptableFilters = [...new Set(filterQueryValues ?? [])];
    return acceptableFilters;
  };

  // Function that will transform filter that have label Range - filters that can have only 2 values in array
  const rangeFilterProcessor = (type: string) => {
    const filterQueryValues = searchParams
      .get(type)
      ?.split(':')
      .map((param) => parseInt(param, 10));
    if (!filterQueryValues || filterQueryValues.length !== 2) return [];
    const filterOptions = RANGE_FILTER_QUERY_OPTIONS[type];
    const { min, max } = filterOptions.options;
    const minQueryParam = filterQueryValues[0];
    const maxQueryParam = filterQueryValues[1];

    function roundToClosest(value: number, options: number[]) {
      return options.reduce((closest, option) => {
        return Math.abs(option - value) < Math.abs(closest - value) ? option : closest;
      });
    }
    const roundedMin = roundToClosest(minQueryParam, min);
    const roundedMax = roundToClosest(maxQueryParam, max);

    const maxIndex = max.findIndex((option) => option === roundedMax);

    const adjustedMax = () => {
      const maxValueInOptions = max[max.length - 1];
      if (max.includes(maxQueryParam)) return maxQueryParam;
      if (maxQueryParam > maxValueInOptions) return maxValueInOptions;
      return max[maxIndex + 1];
    };

    return [roundedMin, adjustedMax()];
    // return [filterQueryValues[0], filterQueryValues[1]];
  };

  // Function that will transform filter that have label Simple - filters that can have only 1 value and it is string
  const simpleFilterProcessor = (type: string) => {
    const filterQueryValues = searchParams.get(type)?.split(',')[0];
    if (filterQueryValues === 'cpo') return 'used';
    return filterQueryValues;
  };

  // Mapping different filters based on the filter type
  const urlParamGetter = (type: string) => {
    if (!searchParams.get(type) && type !== 'type') return [];

    const singleFilterData = QUERY_FILTERS_OPTIONS[type];

    if (singleFilterData?.filterType === 'complex') {
      const complexFilters = complexFiltersProcessor(
        singleFilterData as ComplexFilterOptions,
        type,
      );
      return complexFilters;
    }
    if (singleFilterData?.filterType === 'regular') {
      const regularFilters = regularFilterProcessor(
        singleFilterData as SimpleAndRegularFilterOptions,
        type,
      );
      return regularFilters;
    }
    if (singleFilterData?.filterType === 'range') {
      const rangeFilters = rangeFilterProcessor(type);
      return rangeFilters;
    }
    if (singleFilterData?.filterType === 'simple') {
      const simpleFilter = simpleFilterProcessor(type);
      return simpleFilter;
    }
    if (singleFilterData?.filterType === 'regularNoCheck') {
      const regularFiltersWithoutCheck = regularFilterProcessorWithoutCheck(
        singleFilterData as SimpleAndRegularFilterOptions,
        type,
      );
      return regularFiltersWithoutCheck;
    }
    return [];
  };

  // We are grabbing each individual filter from the query params
  // A little bit more lines, but easy to debug in the future.

  const vehicleType = urlParamGetter('type') as string;
  const bodyStyle = urlParamGetter('body-style') as string[];
  const modelFamily = urlParamGetter('modelFamily') as string[];
  const price = urlParamGetter('price') as number[];
  const year = urlParamGetter('year') as string[];
  const power = urlParamGetter('power') as number[];
  const gearbox = urlParamGetter('gearbox') as string[];
  const drivetrain = urlParamGetter('drivetrain') as string[];
  const fuelType = urlParamGetter('fuel-type') as string[];
  const wheelSize = urlParamGetter('wheel-size') as string[];
  const exteriorColor = urlParamGetter('colour') as string[];
  const interiorColor = urlParamGetter('interior') as string[];
  const trimline = urlParamGetter('trimline') as string[];
  const mileage = urlParamGetter('mileage') as number[];
  const partnerDealers = urlParamGetter('dealer') as string[];
  const nonAudi = urlParamGetter('nonAudi') as string;
  const availableInMonths = urlParamGetter('availableInMonths') as string;
  const usedInventoryType = urlParamGetter('usedInventoryType') as string;
  const otherMakes = urlParamGetter('otherMakes') as string[];
  const nonAudiModels = urlParamGetter('nonAudiModels') as string[];
  const preused = urlParamGetter('preused') as string[];

  const hasAnyAcceptedFilters = !![
    ...modelFamily,
    ...bodyStyle,
    ...price,
    ...year,
    ...power,
    ...gearbox,
    ...drivetrain,
    ...fuelType,
    ...wheelSize,
    ...exteriorColor,
    ...interiorColor,
    ...trimline,
    ...mileage,
    ...nonAudi,
    ...availableInMonths,
    ...usedInventoryType,
    ...preused,
    // ...partnerDealers,
  ].length;

  // Preparing all data for the LS
  const cleanAvailableInMonths = useMemo(() => {
    if (availableInMonths === null || availableInMonths === '' || availableInMonths.length === 0)
      return 3;
    return Number(availableInMonths);
  }, [availableInMonths]);

  const combinedFiltersFromQuery = useMemo(() => {
    return {
      vehicleType: vehicleType || 'new',
      usedInventoryType: usedInventoryType?.length ? usedInventoryType : 'has-warranties.all',
      availableInMonths: cleanAvailableInMonths,
      modelFamily: [...modelFamily],
      bodyType: [...bodyStyle],
      price: [...price],
      modelYear: [...year],
      mileage: [...mileage],
      power: [...power],
      gearBox: [...gearbox],
      drivetrain: [...drivetrain],
      fuelType: [...fuelType],
      wheelSize: [...wheelSize],
      exteriorColor: [...exteriorColor],
      trimLine: [...trimline],
      interiorColor: [...interiorColor],
      partnerDealers: [...partnerDealers],
      nonAudi: nonAudi?.length ? nonAudi : 'foreign-brand.no',
      otherMakes: [...otherMakes],
      nonAudiModels: [...nonAudiModels],
      preused: [...preused],
    };
  }, [
    vehicleType,
    usedInventoryType,
    cleanAvailableInMonths,
    modelFamily,
    bodyStyle,
    price,
    year,
    mileage,
    power,
    gearbox,
    drivetrain,
    fuelType,
    wheelSize,
    exteriorColor,
    trimline,
    interiorColor,
    partnerDealers,
    nonAudi,
    otherMakes,
    nonAudiModels,
    preused,
  ]);

  const listToUpload = useMemo(() => {
    // Tier 3 if there is no params return null
    if (!hasFilterParams) {
      return null;
    }

    // If we have only vhicle Type in URL params, but at the same time we have LS save
    // we are passing onlt Type filter and data from LS

    if (isOnlyFilterTypeParam && filterLSData) {
      return { ...filterLSData, usedInventoryType, vehicleType };
    }
    // If NO prev LS data and no Other filters (except Type) - we are passing
    // Initial filters + Type filter (if no type - default "new")
    if (!filterLSData && !hasAnyAcceptedFilters) {
      return INTIAL_FILTERS;
    }
    // If no other filters and we HAVE data in LS -  we are passing
    // prev values from LS + type (if no - default value "new")
    if (!hasAnyAcceptedFilters && filterLSData) {
      return filterLSData;
    }

    // In all other cases we are passing what we got from the URL params
    return combinedFiltersFromQuery;
  }, [
    hasFilterParams,
    isOnlyFilterTypeParam,
    filterLSData,
    hasAnyAcceptedFilters,
    combinedFiltersFromQuery,
    usedInventoryType,
    vehicleType,
  ]);

  return listToUpload;
};

export default useParseHashFilters;
